import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLayer } from "@/lib/layer-stack";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import { I } from "@/components/Icon";
import { useAutopilotBadge } from "@/lib/api-hooks";
import { useKeywordParserBadge } from "@/lib/keyword-parser-hooks";
import { hasAnyAppAccess, roleBadge } from "@/lib/roles";
import { useAuth } from "@/lib/useAuth";
import {
  useOrgNavigate,
  useOrgPath,
  usePathWithoutPublicId,
} from "@/lib/useOrgNav";
import type { Role } from "@/lib/types";

type NavItemDef = {
  to: string;
  label: string;
  icon: ReactNode;
  roles: readonly Role[];
  badge?: string;
};

// Canonical nav. `roles` gates each item client-side; the backend
// enforces server-side (role decorators on every write endpoint), so
// this is purely UX. Order matches the design mocks.
const NAV: NavItemDef[] = [
  { to: "/overview",  label: "Overview",    icon: I.grid,    roles: ["admin", "ads_manager", "viewer"] },
  { to: "/ads",       label: "Ads Manager", icon: I.sliders, roles: ["admin", "ads_manager", "viewer"] },
  { to: "/aso",       label: "ASO",         icon: I.rank,    roles: ["admin", "ads_manager", "viewer"] },
  { to: "/campaign-studio", label: "Campaign Studio", icon: I.campaignStudio, roles: ["admin", "ads_manager"] },
  { to: "/rules",     label: "Rules",       icon: I.zap,     roles: ["admin", "ads_manager"] },
  { to: "/keywords",  label: "Keyword Parser", icon: I.key,  roles: ["admin", "ads_manager", "viewer"] },
  { to: "/tester",    label: "Tester",      icon: I.flask,   roles: ["admin", "ads_manager", "viewer"] },
  { to: "/autopilot", label: "Autopilot",   icon: I.compass, roles: ["admin", "ads_manager"] },
  { to: "/settings",  label: "Settings",    icon: I.cog,     roles: ["admin", "finance_manager"] },
];

export function Sidebar() {
  const { user } = useAuth();
  const autopilotBadge = useAutopilotBadge();
  // Keyword Parser: the org-level rollout flag hides the section entirely;
  // the badge counts unopened storefront results (same rules as Autopilot).
  const parserBadge = useKeywordParserBadge(!!user?.keyword_parser_enabled);
  const orgNavigate = useOrgNavigate();
  const orgPath = useOrgPath();
  if (!user) return null;

  const financeHasApps =
    user.role === "finance_manager" && hasAnyAppAccess(user);
  const items = NAV.filter((n) => {
    if (n.to === "/keywords" && !user.keyword_parser_enabled) return false;
    if (n.roles.includes(user.role)) return true;
    // Writer-only sections (Campaign Studio / Rules / Autopilot — roles
    // [admin, ads_manager], no 'viewer') also unlock for a per-app writer
    // whose account role is 'viewer' (§51). Admin-only items (Settings,
    // roles [admin]) stay locked — has_app_write never grants those.
    if (
      !!user.has_app_write &&
      n.roles.includes("ads_manager") &&
      !n.roles.includes("viewer")
    )
      return true;
    // Финменеджер, которому выдали рекламу, в читающих разделах —
    // обычный участник: он не должен оказаться ниже него, когда доступ
    // выдан. Без единого приложения в этих разделах для него пусто.
    if (financeHasApps && n.roles.includes("viewer")) return true;
    return false;
  });
  const initials = user.full_name
    ? user.full_name
        .split(" ")
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase()
    : user.email.slice(0, 2).toUpperCase();

  return (
    <aside className="sidebar">
      {/* Brand logo → Overview. Making the logo a nav affordance is
          a basic web convention — users click it expecting "home" and
          were hitting a dead element here. */}
      <button
        type="button"
        onClick={() => orgNavigate("/overview")}
        title="OneAds — back to Overview"
        className="brand"
        style={{
          background: "transparent",
          border: 0,
          padding: "4px 0 10px",
          cursor: "pointer",
        }}
      >
        <div className="brand-mark" />
        <div>
          <div className="brand-name">OneAds</div>
          <div className="brand-sub">Apple Search Ads</div>
        </div>
      </button>

      <OrgSwitcher />

      {/* Workspace nav. Labels stay in the DOM for assistive tech and
          for the hover tooltip; CSS hides them visually in the icons-
          only rail. */}
      <div className="nav-section-label">Workspace</div>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={orgPath(item.to)}
          title={item.label}
          className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
        >
          <span className="nav-icon">
            {item.icon}
            {item.to === "/autopilot" && (autopilotBadge.data ?? 0) > 0 && (
              <span className="nav-count">
                {(autopilotBadge.data ?? 0) > 9 ? "9+" : autopilotBadge.data}
              </span>
            )}
            {item.to === "/keywords" && (parserBadge.data?.count ?? 0) > 0 && (
              <span className="nav-count">
                {(parserBadge.data?.count ?? 0) > 9 ? "9+" : parserBadge.data?.count}
              </span>
            )}
          </span>
          <span>{item.label}</span>
          {item.badge && <span className="nav-badge">{item.badge}</span>}
        </NavLink>
      ))}

      {/* Footer — avatar-only. Clicking it takes the user to the
          Profile settings page where they can review their identity
          and sign out. Inline name / role / logout removed from the
          rail itself because the icons-only layout doesn't have room
          and the redundant Log Out button was a one-click trap. */}
      <div className="sidebar-footer">
        <button
          className="avatar-btn"
          title={`${user.full_name ?? user.email} — ${roleBadge(user.role).label}`}
          onClick={() => orgNavigate("/settings/profile")}
        >
          <span className="avatar">{initials}</span>
        </button>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Org switcher — drops from the sidebar right under the brand mark.
// ---------------------------------------------------------------------------

function OrgSwitcher() {
  const { user, createOrg } = useAuth();
  const navigate = useNavigate();
  const pathWithoutPublicId = usePathWithoutPublicId();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayer(open, () => setOpen(false));

  if (!user) return null;
  const orgs = user.orgs ?? [];
  const active = orgs.find((o) => o.org_id === user.org_id);

  // Pick = navigate to the SAME screen under the new org's public_id.
  // OrgGuard notices the mismatch between URL public_id and JWT
  // scope and runs switchOrg behind the scenes — no explicit call
  // needed here. The URL updates so a link the user shares points
  // at the org they meant.
  const pick = (publicId: number, orgId: number) => {
    if (orgId === user.org_id) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setOpen(false);
    navigate(`/${publicId}${pathWithoutPublicId}`);
    setTimeout(() => setBusy(false), 0);
  };

  const add = async () => {
    const name = window.prompt("Name for the new organization:");
    if (!name) return;
    setBusy(true);
    try {
      const data = await createOrg(name.trim());
      setOpen(false);
      // Navigate to the new org's Overview. Without this the URL is
      // still scoped to the previous org's public_id — OrgGuard sees
      // the mismatch (user.org_id now points at the new org) and
      // stalls in <LoadingScreen/> because the switchOrg-back-to-old
      // flow never resolves cleanly. Pushing the URL to the new org
      // sidesteps the mismatch entirely.
      navigate(`/${data.public_id}/overview`);
    } catch (err) {
      window.alert(
        "Couldn't create the org. " + (err instanceof Error ? err.message : "")
      );
    } finally {
      setBusy(false);
    }
  };

  const orgInitials = (active?.org_name ?? user.org_name ?? "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase() || "?";

  return (
    <div
      ref={ref}
      style={{ position: "relative", margin: "4px 0 8px" }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        title={active?.org_name ?? user.org_name ?? "Organization"}
        style={{
          width: 36,
          height: 36,
          display: "grid",
          placeItems: "center",
          background: "var(--c-surface-sunk)",
          border: "1px solid var(--c-border)",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--c-text)",
          padding: 0,
        }}
      >
        {orgInitials.slice(0, 2)}
      </button>
      {open && createPortal(
        <div
          // Portal into <body> so the popover never lives in the
          // sidebar's stacking context. Topbar uses `backdrop-filter`
          // which spawns its own stacking context and kept pinning
          // the popover underneath content cards regardless of
          // z-index. A body-level portal sidesteps that entirely.
          style={{
            position: "fixed",
            top: (ref.current?.getBoundingClientRect().top ?? 0) - 4,
            left: `calc(var(--sidebar-w) + 8px)`,
            width: 240,
            background: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            borderRadius: 8,
            boxShadow: "var(--sh-3)",
            padding: 4,
            zIndex: 1000,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            style={{
              fontSize: 10,
              color: "var(--c-text-4)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              padding: "6px 10px 4px",
            }}
          >
            Organizations
          </div>
          {orgs.map((o) => (
            <button
              key={o.org_id}
              onClick={() => pick(o.org_public_id, o.org_id)}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 8,
                padding: "7px 10px",
                border: 0,
                background:
                  o.org_id === user.org_id
                    ? "var(--c-accent-50)"
                    : "transparent",
                color:
                  o.org_id === user.org_id
                    ? "var(--c-accent-700)"
                    : "var(--c-text)",
                cursor: "pointer",
                fontSize: 13,
                borderRadius: 6,
                textAlign: "left",
              }}
            >
              <span style={{ flex: 1 }}>{o.org_name}</span>
              <span style={{ color: "var(--c-text-4)", fontSize: 11 }}>
                {roleBadge(o.role).label}
              </span>
            </button>
          ))}
          <div style={{ height: 1, background: "var(--c-divider)", margin: "4px 2px" }} />
          <button
            onClick={() => void add()}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 8,
              padding: "7px 10px",
              border: 0,
              background: "transparent",
              color: "var(--c-accent-600)",
              cursor: "pointer",
              fontSize: 13,
              borderRadius: 6,
              textAlign: "left",
            }}
          >
            + Add organization
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
